import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { TokenStorage } from '../../utils/api';
import AppShell from '../layout/AppShell';
import { usePlaidLink } from 'react-plaid-link';
import { SkeletonAccountCard } from '../common/SkeletonLoader';

const CATEGORY_OPTIONS = [
  { value: 'depository', label: 'Checking/Savings' },
  { value: 'credit', label: 'Credit Card' },
  { value: 'investment', label: 'Investment' },
  { value: 'loan', label: 'Loan' },
];

// Plaid top-level types treated as debt. `loan` covers all loan subtypes
// (auto, student, mortgage, home_equity, line_of_credit, etc.) — matching
// on subtype.includes('loan') would miss most of them because Plaid's
// subtype strings don't contain the word "loan" (e.g., 'auto', 'student').
const DEBT_TYPES = new Set(['credit', 'loan']);

/**
 * Module-level icon resolver — no closures, no per-render recreation.
 * Takes both the Plaid top-level type and subtype to correctly route
 * debt accounts (match on type) vs distinguish checking/savings (match
 * on subtype, which for depository accounts is 'checking' | 'savings').
 */
function getAccountIcon(type, subtype) {
  const t = (type || '').toLowerCase();
  const s = (subtype || '').toLowerCase();
  if (DEBT_TYPES.has(t)) {
    return (
      <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    );
  }
  if (s.includes('checking')) {
    return (
      <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    );
  }
  if (s.includes('savings')) {
    return (
      <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
}

function PlaidLinkAuto({ token, onSuccess, onExit }) {
  const { open, ready } = usePlaidLink({ token, onSuccess, onExit });

  React.useEffect(() => {
    if (ready && typeof open === 'function') open();
  }, [ready, open]);

  return null;
}

/**
 * Normalize various backend shapes into:
 * [{ institution_name, institution_id, institution_logo, accounts: [{ id, name, mask, subtype, account_id, plaid_account_id }] }]
 *
 * Supported payloads:
 * - { items: [{ institution_name, institution_id, institution_logo, accounts: [...] }] }
 * - { accounts: [{ institution_name, institution_id, institution_logo, name, mask, subtype, account_id, plaid_account_id }] }
 */
function normalizePayload(payload) {
  if (!payload) return [];

  // Shape A: { items: [...] }
  if (Array.isArray(payload.items)) {
    return payload.items.map(it => ({
      institution_name: it.institution_name || it.institution || 'Institution',
      institution_id: it.institution_id || it.item_id || undefined,
      institution_logo: it.institution_logo || null,
      // `item_id` is required by `getStatusBadge` and `handleReauthenticate`.
      // Without it, connection-status badges always show "Connected" and the
      // "Re-connect Bank" button never appears even when Plaid reports
      // login_required/error for the item.
      item_id: it.item_id || it.itemId || null,
      accounts: (it.accounts || []).map(a => ({
        id: a.id || a.bank_account_id || a.plaid_account_id || a.account_id,
        name: a.name || a.subtype || 'Account',
        mask: a.mask || '',
        subtype: a.subtype || '',
        type: a.type || '',
        account_id: a.account_id,
        plaid_account_id: a.plaid_account_id
      }))
    }));
  }

  // Shape B: { accounts: [...] } — group by institution_name
  if (Array.isArray(payload.accounts)) {
    const groups = {};
    for (const a of payload.accounts) {
      const key = a.institution_name || 'Institution';
      if (!groups[key]) {
        groups[key] = {
          institution_name: key,
          institution_id: a.institution_id,
          institution_logo: a.institution_logo || null,
          item_id: a.item_id, // Add item_id for re-authentication
          accounts: []
        };
      }
      // Update logo if we get one (in case first account didn't have it)
      if (a.institution_logo && !groups[key].institution_logo) {
        groups[key].institution_logo = a.institution_logo;
      }
      groups[key].accounts.push({
        id: a.id || a.bank_account_id || a.plaid_account_id || a.account_id,
        name: a.name || a.subtype || 'Account',
        mask: a.mask || '',
        subtype: a.subtype || '',
        type: a.type || '',
        account_id: a.account_id,
        plaid_account_id: a.plaid_account_id
      });
    }
    return Object.values(groups);
  }

  // Fallback: nothing recognized
  return [];
}

/**
 * Per-account category override dropdown.
 * Saves via PUT /api/planner/account-settings/:bankAccountId
 */
function CategoryOverrideDropdown({ accountId, plaidType, currentOverride, onSaved }) {
  const [value, setValue] = useState(currentOverride || plaidType || 'depository');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const timerRef = useRef(null);

  // Sync when external data changes (e.g. after reload)
  useEffect(() => {
    setValue(currentOverride || plaidType || 'depository');
  }, [currentOverride, plaidType]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleChange = async (e) => {
    const selected = e.target.value;
    setValue(selected);
    setSaving(true);
    setSaved(false);
    setSaveError(false);

    // If selected matches original Plaid type, clear the override
    const payload = selected === plaidType
      ? { account_category_override: null }
      : { account_category_override: selected };

    try {
      await api.put(`/api/planner/account-settings/${accountId}`, payload);
      setSaved(true);
      if (onSaved) onSaved(accountId, selected === plaidType ? null : selected);
      timerRef.current = setTimeout(() => setSaved(false), 1500);
    } catch (err) {
      console.error('Failed to save category override:', err);
      setSaveError(true);
      // Revert to previous value
      setValue(currentOverride || plaidType || 'depository');
      timerRef.current = setTimeout(() => setSaveError(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const isOverridden = currentOverride && currentOverride !== plaidType;

  return (
    <div className="flex items-center gap-1.5 mt-1.5">
      <label className="text-xs text-gray-500 flex-shrink-0">Category:</label>
      <select
        value={value}
        onChange={handleChange}
        disabled={saving}
        className="text-base sm:text-xs border border-gray-300 rounded px-2 sm:px-1.5 py-1.5 sm:py-0.5 text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 disabled:opacity-50"
      >
        {CATEGORY_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {saving && (
        <svg className="w-3.5 h-3.5 animate-spin text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {saved && (
        <span className="text-xs text-green-600 flex-shrink-0">Saved</span>
      )}
      {saveError && (
        <span className="text-xs text-red-600 flex-shrink-0">Error</span>
      )}
      {!saving && !saved && !saveError && isOverridden && (
        <span className="text-xs text-amber-600 flex-shrink-0">(overridden)</span>
      )}
    </div>
  );
}

export default function BanksPanel() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [itemStatuses, setItemStatuses] = useState({}); // Map of item_id -> status info
  const [accountOverrides, setAccountOverrides] = useState({}); // Map of bank_account_id -> override value
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkToken, setLinkToken] = useState(null);
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [syncNotice, setSyncNotice] = useState(null);

  const handleLogout = async () => {
    try {
      const useCookieFlow = localStorage.getItem('auth_mode') === 'cookie';
      const csrfToken = localStorage.getItem('csrfToken');
      if (useCookieFlow) {
        await api.post('/api/auth/logout', {}, { headers: { 'X-CSRF-Token': csrfToken }, withCredentials: true });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      TokenStorage.removeToken();
      navigate('/logged-out', { replace: true });
    }
  };

  const beginLink = useCallback(async () => {
    try {
      setLinkError(null);
      setLinking(true);
      const res = await api.post('/api/plaid/create-link-token');
      const token = res?.data?.link_token;
      if (!token) {
        throw new Error('Missing link_token from server.');
      }
      setLinkToken(token);
    } catch (e) {
      console.error('beginLink error:', e?.response?.data || e);
      setLinkError('Could not start bank linking. Please try again.');
      setLinking(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch accounts, item statuses, and account settings in parallel
      const [accountsRes, statusRes, settingsRes] = await Promise.allSettled([
        api.get('/api/plaid/accounts-by-institution'),
        api.get('/api/plaid/item-status'),
        api.get('/api/planner/account-settings')
      ]);

      if (accountsRes.status === 'fulfilled') {
        const normalized = normalizePayload(accountsRes.value.data);
        setGroups(normalized);
      } else {
        // Fallback to flat accounts endpoint
        try {
          const res2 = await api.get('/api/plaid/accounts');
          const normalized2 = normalizePayload(res2.data);
          setGroups(normalized2);
        } catch (e2) {
          console.error('BanksPanel load error:', e2?.response?.data || e2);
          setError('Could not load linked banks. Please try again.');
        }
      }

      // Process item statuses
      if (statusRes.status === 'fulfilled') {
        const items = statusRes.value.data?.items || [];
        const statusMap = {};
        items.forEach(item => {
          statusMap[item.item_id] = item;
        });
        setItemStatuses(statusMap);
      }

      // Process account settings for category overrides
      if (settingsRes.status === 'fulfilled') {
        const settings = settingsRes.value.data?.accountSettings || [];
        const overrideMap = {};
        settings.forEach(s => {
          if (s.account_category_override) {
            overrideMap[s.bank_account_id] = s.account_category_override;
          }
        });
        setAccountOverrides(overrideMap);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSuccess = async (public_token, metadata) => {
    try {
      // Pass the full metadata object so backend can extract institution info correctly
      await api.post('/api/plaid/exchange-public-token', {
        public_token,
        metadata,
      });
      await load();
      setSyncNotice('Syncing transactions and detecting bills in the background. This may take a minute.');
      setTimeout(() => setSyncNotice(null), 60000);
    } catch (e) {
      console.error('exchange-public-token error:', e?.response?.data || e);
      setLinkError('Bank linked, but saving failed. Please try refreshing the page.');
    } finally {
      setLinkToken(null);
      setLinking(false);
    }
  };

  const handleExit = () => {
    setLinkToken(null);
    setLinking(false);
  };

  const handleRemoveAccount = async (account, institutionName) => {
    setRemoveConfirm({ 
      accountId: account.id || account.account_id || account.plaid_account_id,
      accountName: account.name,
      institutionName: institutionName 
    });
  };

  const confirmRemove = async () => {
    try {
      await api.delete(`/api/plaid/accounts/${removeConfirm.accountId}`);
      setRemoveConfirm(null);
      await load(); // Refresh after deletion
    } catch (e) {
      console.error('Failed to remove account:', e);
      const errorMsg = e?.response?.data?.error || 'Failed to remove account. Please try again.';
      alert(errorMsg);
      setRemoveConfirm(null);
    }
  };

  const handleReauthenticate = async (itemId) => {
    try {
      setLinkError(null);
      setLinking(true);
      // Create update mode link token
      const res = await api.post('/api/plaid/create-link-token', {
        mode: 'update',
        item_id: itemId
      });
      const token = res?.data?.link_token;
      if (!token) {
        throw new Error('Missing link_token from server.');
      }
      setLinkToken(token);
    } catch (e) {
      console.error('Re-authenticate error:', e?.response?.data || e);
      setLinkError('Could not start re-authentication. Please try again.');
      setLinking(false);
    }
  };

  const handleOverrideSaved = useCallback((bankAccountId, overrideValue) => {
    setAccountOverrides(prev => {
      const next = { ...prev };
      if (overrideValue) {
        next[bankAccountId] = overrideValue;
      } else {
        delete next[bankAccountId];
      }
      return next;
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Helper to get status badge
  const getStatusBadge = (itemId) => {
    const status = itemStatuses[itemId];
    
    if (!status) {
      // Default to connected if no status info
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1">
          ✓ Connected
        </span>
      );
    }

    if (status.status === 'login_required') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 mt-1">
          ⚠️ Re-auth Needed
        </span>
      );
    }

    if (status.status === 'error') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 mt-1">
          ❌ Connection Error
        </span>
      );
    }

    // healthy
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1">
        ✓ Connected
      </span>
    );
  };


  if (loading) {
    return (
      <AppShell onLogout={handleLogout}>
        <div className="space-y-4 sm:space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold font-display text-gray-900">Connected Banks</h2>
              <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your linked bank accounts</p>
            </div>
            <div className="w-32 h-11 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <SkeletonAccountCard />
            <SkeletonAccountCard />
            <SkeletonAccountCard />
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell onLogout={handleLogout}>
      {linkToken && <PlaidLinkAuto token={linkToken} onSuccess={handleSuccess} onExit={handleExit} />}
      
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold font-display text-gray-900">Connected Banks</h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your linked bank accounts</p>
          </div>
          <button
            onClick={beginLink}
            disabled={linking}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-base font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="whitespace-nowrap">{linking ? 'Starting...' : 'Link New Bank'}</span>
          </button>
        </div>

        {/* Error Messages */}
        {linkError && (
          <div className="mb-4 sm:mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 flex items-center justify-between gap-2">
            <span className="text-sm sm:text-base">{linkError}</span>
            <button onClick={() => setLinkError(null)} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-red-700 hover:text-red-900 font-medium">
              ✕
            </button>
          </div>
        )}

        {syncNotice && (
          <div className="mb-4 sm:mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              <span className="text-sm sm:text-base">{syncNotice}</span>
            </div>
            <button onClick={() => setSyncNotice(null)} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-blue-700 hover:text-blue-900 font-medium">
              ✕
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 sm:mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 flex items-center justify-between gap-2">
            <span className="text-sm sm:text-base">{error}</span>
            <button onClick={() => setError(null)} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-red-700 hover:text-red-900 font-medium">
              ✕
            </button>
          </div>
        )}

        {/* Empty State */}
        {!error && groups.length === 0 && (
          <div className="bg-white rounded-lg shadow p-6 sm:p-12 text-center">
            <div className="flex justify-center mb-4">
              <svg className="w-16 h-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
              </svg>
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No banks connected</h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6">Connect your first bank account to start receiving balance notifications</p>
            <button
              onClick={beginLink}
              disabled={linking}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {linking ? 'Starting...' : 'Connect Your First Bank'}
            </button>
          </div>
        )}

        {/* Bank Cards Grid */}
        {!error && groups.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {groups.map((institution, idx) => (
              <div
                key={institution.institution_id || institution.institution_name || idx}
                className="bg-white rounded-lg shadow p-4 sm:p-6 hover:shadow-lg transition-shadow"
              >
                {/* Institution Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {/* Bank Logo - show actual logo if available, otherwise fallback icon */}
                    {institution.institution_logo ? (
                      <img 
                        src={`data:image/png;base64,${institution.institution_logo}`}
                        alt={institution.institution_name}
                        className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-100"
                        onError={(e) => {
                          // Fallback to icon if image fails to load
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 ${institution.institution_logo ? 'hidden' : ''}`}
                    >
                      <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 break-words sm:truncate">
                        {institution.institution_name || 'Institution'}
                      </h3>
                      {/* Connection Status Badge */}
                      {getStatusBadge(institution.item_id)}
                    </div>
                  </div>
                </div>

                {/* Accounts List */}
                <div className="space-y-4 mb-5">
                  {(institution.accounts || []).map((account, i) => (
                    <div
                      key={account.id || account.account_id || account.plaid_account_id || i}
                      className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-100"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getAccountIcon(account.type, account.subtype)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 break-words sm:truncate">
                          {account.name || 'Account'}
                        </div>
                        <div className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                          <span className="capitalize">{account.subtype || 'Account'}</span>
                          {account.mask && (
                            <>
                              <span>•</span>
                              <span>••••{account.mask}</span>
                            </>
                          )}
                        </div>
                        <CategoryOverrideDropdown
                          accountId={account.id}
                          plaidType={account.type || 'depository'}
                          currentOverride={accountOverrides[account.id] || null}
                          onSaved={handleOverrideSaved}
                        />
                      </div>
                      <button
                        onClick={() => handleRemoveAccount(account, institution.institution_name)}
                        className="flex-shrink-0 p-2.5 sm:p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove this account"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>

                {/* Re-authenticate Button - Only show if connection needs attention */}
                {(() => {
                  const status = itemStatuses[institution.item_id];
                  const needsReauth = status && (status.status === 'login_required' || status.status === 'error');
                  
                  if (!needsReauth) return null;
                  
                  return (
                    <div className="pt-4 border-t border-gray-200">
                      <button
                        onClick={() => handleReauthenticate(institution.item_id)}
                        className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-sm sm:text-base text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors font-medium"
                        title="Re-authenticate connection"
                      >
                        Re-connect Bank
                      </button>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Remove Confirmation Modal */}
      {removeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Remove Account?</h3>
              </div>
            </div>
            
            <p className="text-gray-600 mb-2">
              Are you sure you want to remove this account?
            </p>
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <div className="text-sm font-medium text-gray-900">{removeConfirm.accountName}</div>
              <div className="text-sm text-gray-600">{removeConfirm.institutionName}</div>
            </div>
            <p className="text-sm text-amber-600 mb-6">
              ⚠️ This will also delete all notifications associated with this account.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setRemoveConfirm(null)}
                className="flex-1 px-4 py-2.5 sm:py-2 min-h-[44px] bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemove}
                className="flex-1 px-4 py-2.5 sm:py-2 min-h-[44px] bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
              >
                Remove Account
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}